import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  CircularProgress,
  Alert,
  Avatar,
  Chip,
  Button,
  IconButton,
  Drawer,
  Divider,
  Pagination,
  FormControlLabel,
  Checkbox,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useTheme,
  useMediaQuery,
  InputAdornment,
  Tooltip,
  Paper,
  Stack,
  Badge,
  Fade,
  Grow,
  Skeleton,
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
  ExpandMore as ExpandMoreIcon,
  Sort as SortIcon,
  LocationOn as LocationIcon,
  LocationOn,
  School as SchoolIcon,
  MedicalServices as MedicalIcon,
  Close as CloseIcon,
  Star,
  AccessTime,
  Verified,
  Person,
  LocalHospital,
} from '@mui/icons-material';
// Import React Icons
import { FaUserDoctor } from "react-icons/fa6";
import { RiHospitalLine, RiHospitalFill } from "react-icons/ri";
import { IoBookmarksOutline, IoBookmarks } from "react-icons/io5";
// Import images
import clinicIcon from '../assets/clinic.png';
import { useBookMyOpAuth } from '../contexts/BookMyOpAuthContext';
import { useFavorites } from '../contexts/FavoritesContext';
import { useSafeArea } from '../hooks/useSafeArea';
import { Capacitor } from '@capacitor/core';
import { 
  getSpecialties, 
  getDoctors, 
  getServiceProvidersForFilter,
  getServiceProviders 
} from '../services/openapis';
import BookMyOpAPI from '../services/BookMyOpapi';
import ClinicList from './ClinicList';
import DoctorCard from '../components/DoctorCard';
import HospitalCard from '../components/HospitalCard';
import FiltersBar, { getSortOptions } from './Home/FiltersBar';
import { config } from '../config';

// Simple wrapper components that render the full components
const ClinicListContent = () => <ClinicList />;

const FindDoctors = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, loading: authLoading, userLocation } = useBookMyOpAuth();
  const { favoriteDoctorIds, favoriteHospitalIds } = useFavorites();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const safeAreaInsets = useSafeArea();
  const isNativePlatform = Capacitor.isNativePlatform();
  
  // State managementpro
  const [doctors, setDoctors] = useState([]);
  const [hospitals, setHospitals] = useState([]);
  const [specialties, setSpecialties] = useState([]);
  const [serviceProviders, setServiceProviders] = useState([]);
  const [loadingInitial, setLoadingInitial] = useState(true);
  const [loadingDoctors, setLoadingDoctors] = useState(false);
  const [initialDoctorsFetched, setInitialDoctorsFetched] = useState(false);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [isRetrying, setIsRetrying] = useState(false);
  const [lastFetchTime, setLastFetchTime] = useState(0);
  const [cacheKey, setCacheKey] = useState('');
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSpecialty, setSelectedSpecialty] = useState('');
  const [selectedServiceProvider, setSelectedServiceProvider] = useState('');
  const [selectedQualifications, setSelectedQualifications] = useState('');
  const [sortBy, setSortBy] = useState([]); // Changed to array for multiple sort options

  // Memoize sortBy array as string for stable dependency comparison
  const sortByKey = React.useMemo(() => JSON.stringify([...sortBy].sort()), [sortBy]);
  
  // Date filter state
  const [availableDate, setAvailableDate] = useState('');
  const [availableDateStart, setAvailableDateStart] = useState('');
  const [availableDateEnd, setAvailableDateEnd] = useState('');
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize] = useState(20); // Updated to match API default pageSize
  
  // UI states
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [activeFilters, setActiveFilters] = useState([]);
  const [searchBarOpen, setSearchBarOpen] = useState(false);
  
  // Track if initial URL parameters have been processed
  const [initialUrlProcessed, setInitialUrlProcessed] = useState(false);
  
  // Debounced search term
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  const [listParam, setListParam] = useState(searchParams.get('list'));
  const [selectedList, setSelectedList] = useState(listParam || 'doctors');
  
  // Saved favorites state (for displaying in saved tab)
  const [savedDoctors, setSavedDoctors] = useState([]);
  const [savedHospitals, setSavedHospitals] = useState([]);
  const [loadingSaved, setLoadingSaved] = useState(false);
  const [savedError, setSavedError] = useState(null);
  const fetchingSavedRef = useRef(false);
  const fetchingDoctorsRef = useRef(false);

  // Fetch saved favorites
  const fetchSavedFavorites = useCallback(async () => {
    // Prevent duplicate concurrent calls
    if (fetchingSavedRef.current) {
      console.log('⏭️ Already fetching saved favorites, skipping duplicate call...');
      return;
    }
    
    try {
      fetchingSavedRef.current = true;
      setLoadingSaved(true);
      setSavedError(null);
      
      console.log('🔖 Fetching saved favorites...');
      
      // Fetch both doctors and hospitals in parallel
      const [doctorsResponse, hospitalsResponse] = await Promise.allSettled([
        BookMyOpAPI.getFavoriteDoctors(),
        BookMyOpAPI.getFavoriteProviders()
      ]);
      
      // Handle doctors response
      if (doctorsResponse.status === 'fulfilled') {
        const doctorsData = doctorsResponse.value;
        console.log('✅ Full Doctors response:', doctorsData);
        
        // Extract doctors from the response - check multiple possible structures
        let doctors = [];
        if (Array.isArray(doctorsData)) {
          doctors = doctorsData;
        } else if (doctorsData.favorites && Array.isArray(doctorsData.favorites)) {
          // API returns { favorites: [...], count: X }
          doctors = doctorsData.favorites;
        } else if (doctorsData.results && Array.isArray(doctorsData.results)) {
          doctors = doctorsData.results;
        } else if (doctorsData.data) {
          if (Array.isArray(doctorsData.data)) {
            doctors = doctorsData.data;
          } else if (doctorsData.data.favorites && Array.isArray(doctorsData.data.favorites)) {
            doctors = doctorsData.data.favorites;
          } else if (doctorsData.data.results && Array.isArray(doctorsData.data.results)) {
            doctors = doctorsData.data.results;
          }
        }
        
        // Extract doctor objects from favorites - filter by type and extract nested doctor
        const extractedDoctors = doctors
          .filter(item => item.favorite_type === 'doctor' && item.doctor)
          .map(item => item.doctor)
          .filter(doc => doc && doc.id);
        
        setSavedDoctors(extractedDoctors);
        console.log('✅ Saved doctors count:', extractedDoctors.length);
        console.log('✅ Saved doctors:', extractedDoctors);
      } else {
        console.error('❌ Failed to fetch favorite doctors:', doctorsResponse.reason);
        setSavedDoctors([]);
      }
      
      // Handle hospitals response
      if (hospitalsResponse.status === 'fulfilled') {
        const hospitalsData = hospitalsResponse.value;
        console.log('✅ Full Hospitals response:', hospitalsData);
        
        // Extract hospitals from the response - check multiple possible structures
        let hospitals = [];
        if (Array.isArray(hospitalsData)) {
          hospitals = hospitalsData;
        } else if (hospitalsData.favorites && Array.isArray(hospitalsData.favorites)) {
          // API returns { favorites: [...], count: X }
          hospitals = hospitalsData.favorites;
        } else if (hospitalsData.results && Array.isArray(hospitalsData.results)) {
          hospitals = hospitalsData.results;
        } else if (hospitalsData.data) {
          if (Array.isArray(hospitalsData.data)) {
            hospitals = hospitalsData.data;
          } else if (hospitalsData.data.favorites && Array.isArray(hospitalsData.data.favorites)) {
            hospitals = hospitalsData.data.favorites;
          } else if (hospitalsData.data.results && Array.isArray(hospitalsData.data.results)) {
            hospitals = hospitalsData.data.results;
          }
        }
        
        // Extract hospital/provider objects from favorites - filter by type and extract nested provider
        const extractedHospitals = hospitals
          .filter(item => item.favorite_type === 'service_provider' && item.service_provider)
          .map(item => item.service_provider)
          .filter(hosp => hosp && hosp.id);
        
        setSavedHospitals(extractedHospitals);
        console.log('✅ Saved hospitals count:', extractedHospitals.length);
        console.log('✅ Saved hospitals:', extractedHospitals);
      } else {
        console.error('❌ Failed to fetch favorite hospitals:', hospitalsResponse.reason);
        setSavedHospitals([]);
      }
      
    } catch (error) {
      console.error('❌ Error fetching saved favorites:', error);
      setSavedError('Failed to load saved favorites');
      setSavedDoctors([]);
      setSavedHospitals([]);
    } finally {
      setLoadingSaved(false);
      fetchingSavedRef.current = false;
    }
  }, []);

  // Sync selectedList with URL param or route
  useEffect(() => {
    if (isMobile) {
      // On mobile, determine selected list from current route
      const pathname = location.pathname;
      if (pathname === '/find-doctors/clinics') {
        setSelectedList('clinics');
      } else if (pathname === '/find-doctors/hospitals') {
        setSelectedList('hospitals');
      } else if (pathname === '/find-doctors/saved') {
        setSelectedList('saved');
        // Fetch saved data when navigating to saved tab
        fetchSavedFavorites();
      } else if (pathname === '/find-doctors') {
        setSelectedList('doctors');
      }
    } else {
      // Desktop behavior - use URL params
      if (listParam && listParam !== selectedList) {
        setSelectedList(listParam);
        // Fetch saved data when switching to saved tab on desktop
        if (listParam === 'saved') {
          fetchSavedFavorites();
        }
      }
    }
    // eslint-disable-next-line
  }, [listParam, location.pathname, isMobile]);

  const handleListChange = (list) => {
    setSelectedList(list);
    
    // On mobile, change routes instead of URL params
    if (isMobile) {
      if (list === 'doctors') {
        navigate('/find-doctors');
      } else if (list === 'clinics') {
        navigate('/find-doctors/clinics');
      } else if (list === 'hospitals') {
        navigate('/find-doctors/hospitals');
      } else if (list === 'saved') {
        navigate('/find-doctors/saved');
      }
    } else {
      // Desktop behavior - use URL params
      const params = new URLSearchParams(searchParams);
      if (list === 'doctors') {
        params.delete('list');
      } else {
        params.set('list', list);
      }
      setSearchParams(params, { replace: true });
    }
    
    // Note: fetchSavedFavorites is handled by useEffect when route/params change
    // This prevents duplicate API calls
  };

  // Safe area styles for mobile
  const safeAreaStyles = {
    paddingTop: isMobile ? `${safeAreaInsets.top}px` : '16px',
    paddingBottom: isMobile ? `${safeAreaInsets.bottom + 16}px` : '16px',
    paddingLeft: isMobile ? `${safeAreaInsets.left + 16}px` : '16px',
    paddingRight: isMobile ? `${safeAreaInsets.right + 16}px` : '16px',
    minHeight: '100vh',
    minHeight: '100dvh',
  };

  // Utility function to get safe area styles
  const getSafeAreaPadding = (basePadding = 2) => {
    // Apply more aggressive safe area handling for native platforms
    const multiplier = isNativePlatform ? 1 : 1;
    
    return {
      paddingTop: isMobile ? `${safeAreaInsets.top + (basePadding * multiplier)}px` : `${basePadding}px`,
      paddingBottom: isMobile ? `${safeAreaInsets.bottom + (basePadding * multiplier)}px` : `${basePadding}px`,
      paddingLeft: isMobile ? `${safeAreaInsets.left + (basePadding * multiplier)}px` : `${basePadding}px`,
      paddingRight: isMobile ? `${safeAreaInsets.right + (basePadding * multiplier)}px` : `${basePadding}px`,
    };
  };

  // Common qualifications for filter
  const commonQualifications = [
    'MBBS', 'MD', 'MS', 'DM', 'MCh', 'DNB', 'FRCS', 'MRCP', 'FRCP', 'FACS',
    'Diploma', 'Fellowship', 'Specialist', 'Consultant'
  ];

  // Sort options
  const sortOptions = [
    { value: 'name', label: 'Name (A-Z)' },
    { value: '-name', label: 'Name (Z-A)' },
    { value: 'experience_years', label: 'Experience (Low to High)' },
    { value: '-experience_years', label: 'Experience (High to Low)' },
    { value: 'consultation_fee', label: 'Fee (Low to High)' },
    { value: '-consultation_fee', label: 'Fee (High to Low)' },
  ];

  // Function to find specialty ID by name
  const findSpecialtyIdByName = (specialtyName, specialtiesList) => {
    if (!specialtyName || !specialtiesList.length) return '';
    
    const specialty = specialtiesList.find(s => 
      s.name.toLowerCase().includes(specialtyName.toLowerCase()) ||
      specialtyName.toLowerCase().includes(s.name.toLowerCase())
    );
    
    return specialty ? specialty.id : '';
  };

  // Function to find service provider ID by name
  const findServiceProviderIdByName = (providerName, providersList) => {
    if (!providerName || !providersList.length) return '';
    
    const provider = providersList.find(p => 
      p.name.toLowerCase().includes(providerName.toLowerCase()) ||
      providerName.toLowerCase().includes(p.name.toLowerCase())
    );
    
    return provider ? provider.id : '';
  };

  // Helper function to get human-readable sort label
  const getSortLabel = (sortValue) => {
    // Map the new sorting options
    const sortLabelMap = {
      '-star_rating': 'Rating wise',
      '-experience_years': 'Seniority wise',
      'consultation_fee': 'Low cost wise',
      'distance_km': 'Nearest to my location',
    };
    
    // Check if it's in our map
    if (sortLabelMap[sortValue]) {
      return `Sort: ${sortLabelMap[sortValue]}`;
    }
    
    // Fallback to sortOptions array
    const option = sortOptions.find(opt => opt.value === sortValue);
    return option ? `Sort: ${option.label}` : `Sort: ${sortValue}`;
  };


  // Update active filters display
  const updateActiveFilters = useCallback(() => {
    const filters = [];
    if (searchTerm) filters.push({ type: 'search', label: `Search: "${searchTerm}"` });
    if (selectedSpecialty) {
      const specialty = specialties.find(s => s.id === selectedSpecialty);
      if (specialty) filters.push({ type: 'specialty', label: `Specialty: ${specialty.name}` });
    }
    if (selectedServiceProvider) {
      const provider = serviceProviders.find(p => p.id === selectedServiceProvider);
      if (provider) filters.push({ type: 'provider', label: `Provider: ${provider.name}` });
    }
    if (selectedQualifications) {
      filters.push({ type: 'qualifications', label: `Qualification: ${selectedQualifications}` });
    }
    if (availableDate) {
      filters.push({ type: 'date', label: `Date: ${new Date(availableDate).toLocaleDateString()}` });
    }
    if (availableDateStart && availableDateEnd) {
      filters.push({ type: 'dateRange', label: `Date Range: ${new Date(availableDateStart).toLocaleDateString()} - ${new Date(availableDateEnd).toLocaleDateString()}` });
    } else if (availableDateStart) {
      filters.push({ type: 'dateRange', label: `From: ${new Date(availableDateStart).toLocaleDateString()}` });
    } else if (availableDateEnd) {
      filters.push({ type: 'dateRange', label: `Until: ${new Date(availableDateEnd).toLocaleDateString()}` });
    }
    // Add sort filter if it's set
    if (sortBy && sortBy.length > 0) {
      const sortLabels = sortBy.map(sortOption => getSortLabel(sortOption)).filter(label => label !== 'Sort: ');
      if (sortLabels.length > 0) {
        filters.push({
          type: 'sort',
          label: sortLabels.length === 1 ? sortLabels[0] : `Sort: ${sortLabels.join(', ')}`
        });
      }
    }
    setActiveFilters(filters);
  }, [searchTerm, selectedSpecialty, selectedServiceProvider, selectedQualifications, availableDate, availableDateStart, availableDateEnd, sortByKey, specialties, serviceProviders]);

  // Clear specific filter
  const clearFilter = (filterType) => {
    switch (filterType) {
      case 'search':
        setSearchTerm('');
        break;
      case 'specialty':
        setSelectedSpecialty('');
        break;
      case 'provider':
        setSelectedServiceProvider('');
        break;
      case 'qualifications':
        setSelectedQualifications('');
        break;
      case 'date':
      case 'dateRange':
        setAvailableDate('');
        setAvailableDateStart('');
        setAvailableDateEnd('');
        break;
      case 'sort':
        setSortBy([]); // Clear array
        break;
      default:
        break;
    }
  };

  // Clear all filters
  const clearAllFilters = () => {
    setSearchTerm('');
    setSelectedSpecialty('');
    setSelectedServiceProvider('');
    setSelectedQualifications('');
    setAvailableDate('');
    setAvailableDateStart('');
    setAvailableDateEnd('');
    setSortBy([]); // Clear array
    setCurrentPage(1);
  };

  // Retry function for failed API calls
  const retryFetch = async () => {
    if (isRetrying) return;
    
    setIsRetrying(true);
    setRetryCount(prev => prev + 1);
    setError(null);
    
    try {
      // Add delay for retry attempts to avoid rate limiting
      if (retryCount > 0) {
        await new Promise(resolve => setTimeout(resolve, Math.min(1000 * Math.pow(2, retryCount), 5000)));
      }
      
      // Retry fetching doctors using dedicated Doctors API
      const filters = {
        page: currentPage,
        pageSize: pageSize,
      };
      
      if (searchTerm) filters.search = searchTerm;
      if (selectedSpecialty) filters.specialty = selectedSpecialty;
      if (selectedServiceProvider) filters.serviceProvider = selectedServiceProvider;
      if (selectedQualifications) filters.qualifications = selectedQualifications;
      if (sortBy) filters.ordering = sortBy;
      
      const response = await getDoctors(filters);
      
      // Handle paginated response
      let doctorsData = [];
      let totalCount = 0;
      let actualPageSize = pageSize; // Default to requested pageSize
      
      if (response.results) {
        doctorsData = response.results;
        totalCount = response.count || doctorsData.length;
        
        // Detect actual pageSize from API response
        if (response.page_size) {
          actualPageSize = response.page_size;
        } else if (doctorsData.length > 0 && response.next) {
          actualPageSize = doctorsData.length;
        } else if (doctorsData.length > 0 && currentPage === 1 && doctorsData.length !== pageSize) {
          actualPageSize = doctorsData.length;
        }
      } else if (Array.isArray(response)) {
        doctorsData = response;
        totalCount = response.length;
        actualPageSize = response.length;
      } else {
        throw new Error('Invalid response format from doctors API');
      }
      
      // Validate and transform doctors data
      const validatedDoctors = validateDoctorsArray(doctorsData);
      setDoctors(validatedDoctors);
      
      // Use actual pageSize for pagination calculation
      setTotalPages(Math.ceil(totalCount / actualPageSize));
      setTotalCount(totalCount);
      
      console.log('Retry successful with dedicated Doctors API:', { count: validatedDoctors.length, total: totalCount });
      
    } catch (err) {
      console.error('Retry failed:', err);
      
      // Handle specific retry errors
      if (err.response?.status === 429) {
        setError('Too many requests. Please wait a moment and try again.');
      } else if (err.response?.status >= 500) {
        setError('Server error. Please try again later.');
      } else {
        setError('Retry failed. Please check your connection and try again.');
      }
    } finally {
      setIsRetrying(false);
    }
  };

  // Fetch initial data - only run once on mount
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setLoadingInitial(true);
        setError(null);
        
        console.log('Fetching initial data...'); // Debug log
        
        // Fetch all data in parallel with individual error handling
        const [specialtiesData, providersData] = await Promise.allSettled([
          getSpecialties(),
          getServiceProvidersForFilter()
        ]);
        
        // Handle specialties data
        let specialtiesList = [];
        if (specialtiesData.status === 'fulfilled') {
          const data = specialtiesData.value;
          specialtiesList = data.results || data.data || data || [];
          console.log('Specialties fetched:', specialtiesList.length);
        } else {
          console.error('Failed to fetch specialties:', specialtiesData.reason);
        }
        
        // Handle service providers data
        let providersList = [];
        if (providersData.status === 'fulfilled') {
          const data = providersData.value;
          providersList = data.results || data.data || data || [];
          console.log('Service providers fetched:', providersList.length);
        } else {
          console.error('Failed to fetch service providers:', providersData.reason);
        }
        
        setSpecialties(specialtiesList);
        setServiceProviders(providersList);
        
        // Only set error if both requests failed
        if (specialtiesData.status === 'rejected' && providersData.status === 'rejected') {
          setError('Failed to fetch initial data. Please try again later.');
        } else if (specialtiesData.status === 'rejected') {
          setError('Failed to fetch specialties. Some features may not work properly.');
        } else if (providersData.status === 'rejected') {
          setError('Failed to fetch service providers. Some features may not work properly.');
        }
        
        console.log('Initial data fetch completed');
        
      } catch (err) {
        console.error('Unexpected error in fetchInitialData:', err);
        setError('An unexpected error occurred. Please try again later.');
      } finally {
        setLoadingInitial(false);
      }
    };

    fetchInitialData();
  }, []); // Remove searchParams dependency to prevent loop

  // Handle initial URL parameters after data is loaded
  useEffect(() => {
    // Only process URL parameters if we have the necessary data loaded and haven't processed them yet
    if (specialties.length === 0 || serviceProviders.length === 0 || initialUrlProcessed) {
      return;
    }
        
        // Check for URL parameters
        const specialtyParam = searchParams.get('specialty');
        const searchParam = searchParams.get('search');
        const providerParam = searchParams.get('provider');
        const qualificationsParam = searchParams.get('qualifications');
        const sortParam = searchParams.get('sort');
        const pageParam = searchParams.get('page');
        const availableDateParam = searchParams.get('availableDate');
        const availableDateStartParam = searchParams.get('availableDateStart');
        const availableDateEndParam = searchParams.get('availableDateEnd');
        
        let initialSpecialtyId = '';
        let initialProviderId = '';
        
        // Handle specialty parameter
    if (specialtyParam && specialties.length > 0) {
      initialSpecialtyId = findSpecialtyIdByName(specialtyParam, specialties);
          if (initialSpecialtyId) {
            setSelectedSpecialty(initialSpecialtyId);
          }
        }
        
        // Handle service provider parameter
    if (providerParam && serviceProviders.length > 0) {
          // First check if providerParam is a valid ID
      const providerById = serviceProviders.find(p => p.id.toString() === providerParam);
          if (providerById) {
            setSelectedServiceProvider(providerById.id);
          } else {
            // If not found by ID, try to find by name
        initialProviderId = findServiceProviderIdByName(providerParam, serviceProviders);
            if (initialProviderId) {
              setSelectedServiceProvider(initialProviderId);
            }
          }
        }
        
        // Handle other parameters
        if (searchParam) setSearchTerm(searchParam);
        if (qualificationsParam) setSelectedQualifications(qualificationsParam);
        if (sortParam) {
          // Support multiple sort options as comma-separated values
          const sortArray = sortParam.split(',').map(s => s.trim());
          setSortBy(sortArray);
        }
        if (pageParam) setCurrentPage(parseInt(pageParam));
        if (availableDateParam) setAvailableDate(availableDateParam);
        if (availableDateStartParam) setAvailableDateStart(availableDateStartParam);
        if (availableDateEndParam) setAvailableDateEnd(availableDateEndParam);
        
    // Mark as processed AFTER a small delay to ensure state updates are applied
    // This ensures the fetch doctors useEffect will see the updated state values
    setTimeout(() => {
      setInitialUrlProcessed(true);
      console.log('✅ URL parameters processed and state updated');
    }, 100);
    
  }, [specialties.length, serviceProviders.length, initialUrlProcessed, searchParams]); // Add searchParams to dependencies

  // Debounce search term to prevent excessive API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500); // 500ms debounce

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch doctors with filters and pagination using dedicated Doctors API
  useEffect(() => {
    // Skip if specialties not loaded yet or not on doctors tab
    // Only wait for URL params on the very first load (when initialUrlProcessed is false and we have URL params)
    const hasUrlParams = searchParams.toString().length > 0;
    if (specialties.length === 0 || selectedList !== 'doctors') {
      console.log('Skipping doctors fetch:', { 
        specialtiesLoaded: specialties.length > 0, 
        selectedList
      });
      return;
    }
    
    // If we have URL params but haven't processed them yet, wait
    if (hasUrlParams && !initialUrlProcessed) {
      console.log('⏳ Waiting for URL parameters to be processed...');
      return;
    }
    
    const fetchDoctors = async () => {
      // Prevent concurrent API calls
      if (fetchingDoctorsRef.current) {
        console.log('⏭️ Already fetching doctors, skipping duplicate call...');
        return;
      }

      try {
        fetchingDoctorsRef.current = true;
        setLoadingDoctors(true);
        setError(null);
        
        // Build filter parameters for dedicated Doctors API
        const filters = {
          page: currentPage,
          pageSize: pageSize,
        };
        
        // Add search term (use debouncedSearchTerm if available, otherwise searchTerm)
        const searchValue = debouncedSearchTerm || searchTerm;
        if (searchValue) {
          filters.search = searchValue;
        }
        
        // Add specialty filter
        if (selectedSpecialty) {
          filters.specialty = selectedSpecialty;
        }
        
        // Add service provider filter
        if (selectedServiceProvider) {
          filters.serviceProvider = selectedServiceProvider;
        }
        
        // Add qualifications filter
        if (selectedQualifications) {
          filters.qualifications = selectedQualifications;
        }
        
        // Add date filters
        if (availableDate) {
          filters.availableDate = availableDate;
        } else if (availableDateStart || availableDateEnd) {
          if (availableDateStart) filters.availableDateStart = availableDateStart;
          if (availableDateEnd) filters.availableDateEnd = availableDateEnd;
        }
        
        // Add sorting - use first selected option if multiple are selected
        if (sortBy && sortBy.length > 0) {
          filters.ordering = sortBy[0]; // Use first selected sort option
        }
        
        console.log('🔍 Fetching doctors with filters:', filters, 'sortByKey:', sortByKey); // Enhanced debug log
        console.log('🔍 Current state values:', {
          selectedSpecialty,
          availableDate,
          availableDateStart,
          availableDateEnd,
          searchTerm: searchValue,
          sortBy: sortBy,
          initialUrlProcessed
        });
        
        // Call the real API
        const response = await getDoctors(filters);
        
        console.log('✅ API Response received:', {
          hasResults: !!response.results,
          isArray: Array.isArray(response),
          count: response.count,
          resultsLength: response.results?.length || (Array.isArray(response) ? response.length : 0)
        });
        
        // Handle paginated response
        let doctorsData = [];
        let totalCount = 0;
        let actualPageSize = pageSize; // Default to requested pageSize
        
        if (response.results) {
          doctorsData = response.results;
          totalCount = response.count || doctorsData.length;
          
          // Detect actual pageSize from API response
          // If API returns page_size in response, use it
          if (response.page_size) {
            actualPageSize = response.page_size;
          } 
          // Otherwise, infer from results count (if we have a full page and there's a next page)
          else if (doctorsData.length > 0 && response.next) {
            actualPageSize = doctorsData.length;
          }
          // On first page with results, use the count if it's different from requested
          else if (doctorsData.length > 0 && currentPage === 1 && doctorsData.length !== pageSize) {
            actualPageSize = doctorsData.length;
          }
        } else if (Array.isArray(response)) {
          doctorsData = response;
          totalCount = response.length;
          actualPageSize = response.length; // Use actual count for array responses
        } else {
          throw new Error('Invalid response format from doctors API');
        }
        
        // Validate and transform doctors data
        const validatedDoctors = validateDoctorsArray(doctorsData);
        setDoctors(validatedDoctors);
        
        // Use actual pageSize for pagination calculation
        setTotalPages(Math.ceil(totalCount / actualPageSize));
        setTotalCount(totalCount);
        
        // Update cache
        const currentCacheKey = generateCacheKey(filters);
        setCacheKey(currentCacheKey);
        setLastFetchTime(Date.now());
        
        console.log('✅ Doctors fetched successfully:', {
          count: validatedDoctors.length,
          total: totalCount,
          page: currentPage,
          filters: filters
        });
        
      } catch (err) {
        console.error('Error fetching doctors:', err);
        
        // Handle different error types
        let errorMessage = 'Failed to fetch doctors. Please try again later.';
        
        if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
          errorMessage = 'Request timeout. Please check your connection and try again.';
        } else if (err.code === 'NETWORK_ERROR' || !err.response) {
          errorMessage = 'Network error. Please check your internet connection.';
        } else if (err.response?.status === 401) {
          errorMessage = 'Authentication required. Please login again.';
        } else if (err.response?.status === 403) {
          errorMessage = 'Access denied. Please check your permissions.';
        } else if (err.response?.status === 404) {
          errorMessage = 'Doctors service not found. Please try again later.';
        } else if (err.response?.status === 429) {
          errorMessage = 'Too many requests. Please wait a moment and try again.';
        } else if (err.response?.status >= 500) {
          errorMessage = 'Server error. Please try again later.';
        } else if (err.message) {
          errorMessage = err.message;
        }
        
        setError(errorMessage);
        setDoctors([]);
        setTotalPages(1);
        setTotalCount(0);
      } finally {
        setLoadingDoctors(false);
        setInitialDoctorsFetched(true);
        fetchingDoctorsRef.current = false;
      }
    };

    // Call fetchDoctors immediately (debouncing is already handled for search term)
    fetchDoctors();
  }, [selectedList, debouncedSearchTerm, searchTerm, selectedSpecialty, selectedServiceProvider, selectedQualifications, sortByKey, currentPage, pageSize, specialties.length, availableDate, availableDateStart, availableDateEnd, initialUrlProcessed]);

  // Fetch hospitals with filters and pagination
  useEffect(() => {
    if (selectedList !== 'hospitals') {
      console.log('Skipping hospitals fetch:', { selectedList });
      return;
    }
    
    const fetchHospitals = async () => {
      try {
        setLoadingDoctors(true);
        setError(null);
        
        console.log('🏥 Fetching hospitals with API');
        
        // Build filter parameters for hospitals API
        const hospitalFilters = {
          page: currentPage,
          pageSize: pageSize,
        };
        
        // Add search term (use debouncedSearchTerm if available, otherwise searchTerm)
        const searchValue = debouncedSearchTerm || searchTerm;
        if (searchValue) {
          hospitalFilters.search = searchValue;
        }
        
        // Add specialty filter (hospitals can also be filtered by specialty)
        if (selectedSpecialty) {
          hospitalFilters.specialty = selectedSpecialty;
        }
        
        // Add date filters
        if (availableDate) {
          hospitalFilters.availableDate = availableDate;
        } else if (availableDateStart || availableDateEnd) {
          if (availableDateStart) hospitalFilters.availableDateStart = availableDateStart;
          if (availableDateEnd) hospitalFilters.availableDateEnd = availableDateEnd;
        }
        
        // Add sorting
        if (sortBy) {
          hospitalFilters.ordering = sortBy;
        }
        
        console.log('🏥 Fetching hospitals with filters:', hospitalFilters);
        
        // Call the real API for hospitals with filters
        const response = await getServiceProviders(hospitalFilters);
        
        console.log('✅ Hospitals API Response received:', {
          hasResults: !!response.results,
          isArray: Array.isArray(response),
          count: response.count,
          resultsLength: response.results?.length || (Array.isArray(response) ? response.length : 0)
        });
        
        // Handle paginated response
        let hospitalsData = [];
        let totalCount = 0;
        let actualPageSize = pageSize; // Default to requested pageSize
        
        if (response.results) {
          hospitalsData = response.results;
          totalCount = response.count || hospitalsData.length;
          
          // Detect actual pageSize from API response
          // If API returns page_size in response, use it
          if (response.page_size) {
            actualPageSize = response.page_size;
          } 
          // Otherwise, infer from results count (if we have a full page and there's a next page)
          else if (hospitalsData.length > 0 && response.next) {
            actualPageSize = hospitalsData.length;
          }
          // On first page with results, use the count if it's different from requested
          else if (hospitalsData.length > 0 && currentPage === 1 && hospitalsData.length !== pageSize) {
            actualPageSize = hospitalsData.length;
          }
        } else if (Array.isArray(response)) {
          hospitalsData = response;
          totalCount = response.length;
          actualPageSize = response.length; // Use actual count for array responses
        } else {
          throw new Error('Invalid response format from hospitals API');
        }
        
        setHospitals(hospitalsData);
        // Use actual pageSize for pagination calculation
        setTotalPages(Math.ceil(totalCount / actualPageSize));
        setTotalCount(totalCount);
        
        console.log('✅ Hospitals fetched successfully:', {
          count: hospitalsData.length,
          total: totalCount,
          page: currentPage,
          filters: hospitalFilters
        });
        
      } catch (err) {
        console.error('Error fetching hospitals:', err);
        setError('Failed to fetch hospitals. Please try again later.');
        setHospitals([]);
        setTotalPages(1);
        setTotalCount(0);
      } finally {
        setLoadingDoctors(false);
      }
    };

    // Call fetchHospitals immediately (debouncing is already handled for search term)
    fetchHospitals();
  }, [selectedList, debouncedSearchTerm, searchTerm, selectedSpecialty, sortByKey, currentPage, pageSize, availableDate, availableDateStart, availableDateEnd]);

  // Update active filters when filters change
  useEffect(() => {
    updateActiveFilters();
  }, [updateActiveFilters]);

  // Listen for favorites updates (only refresh saved tab if we're on it)
  useEffect(() => {
    const handleFavoritesUpdate = () => {
      if (selectedList === 'saved') {
        console.log('🔄 Favorites updated, refreshing saved tab...');
        fetchSavedFavorites();
      }
    };

    window.addEventListener('favoritesUpdated', handleFavoritesUpdate);
    return () => window.removeEventListener('favoritesUpdated', handleFavoritesUpdate);
  }, [selectedList, fetchSavedFavorites]);

  // Ensure sortBy is valid for current selectedList (only validate if sortBy has values)
  useEffect(() => {
    if ((selectedList === 'doctors' || selectedList === 'hospitals') && sortBy && sortBy.length > 0) {
      const currentType = selectedList === 'doctors' ? 'doctors' : 'hospitals';
      const validOptions = getSortOptions(currentType, userLocation);
      const validOptionValues = validOptions.map(opt => opt.value);

      // Filter out invalid options
      const validSortBy = sortBy.filter(sortOption => validOptionValues.includes(sortOption));

      // If some options were invalid, update the state
      if (validSortBy.length !== sortBy.length) {
        console.log('Filtering invalid sort options:', sortBy, '->', validSortBy);
        setSortBy(validSortBy);
      }
    }
  }, [selectedList, userLocation, sortBy]);

  // Update URL parameters when filters change
  useEffect(() => {
    // Only update URL if we have the necessary data loaded
    if (specialties.length === 0 || serviceProviders.length === 0) {
      return;
    }

    const params = new URLSearchParams();
    
    if (searchTerm) params.set('search', searchTerm);
    if (selectedSpecialty) {
      const specialty = specialties.find(s => s.id === selectedSpecialty);
      if (specialty) params.set('specialty', specialty.name);
    }
    if (selectedServiceProvider) {
      const provider = serviceProviders.find(p => p.id === selectedServiceProvider);
      if (provider) {
        // Use ID for provider parameter to maintain consistency
        params.set('provider', provider.id.toString());
      }
    }
    if (selectedQualifications) params.set('qualifications', selectedQualifications);
    // Only include sort parameter if it has values
    if (sortBy && sortBy.length > 0) params.set('sort', sortBy.join(','));
    if (currentPage > 1) params.set('page', currentPage.toString());
    if (availableDate) params.set('availableDate', availableDate);
    if (availableDateStart) params.set('availableDateStart', availableDateStart);
    if (availableDateEnd) params.set('availableDateEnd', availableDateEnd);
    
    setSearchParams(params, { replace: true });
  }, [searchTerm, selectedSpecialty, selectedServiceProvider, selectedQualifications, sortBy, currentPage, availableDate, availableDateStart, availableDateEnd, setSearchParams]);

  const handleDoctorClick = (doctorId) => {
    if (!doctorId) {
      console.error('Doctor ID is undefined');
      return;
    }
    
    // Pass date filters if they exist so DoctorDetail can pre-fill the date picker
    const dateFilters = {};
    if (availableDate) {
      dateFilters.availableDate = availableDate;
    } else if (availableDateStart) {
      dateFilters.availableDateStart = availableDateStart;
    } else if (availableDateEnd) {
      dateFilters.availableDateEnd = availableDateEnd;
    }
    
    navigate(`/doctors/${doctorId}`, { 
      state: { 
        doctorId,
        ...(Object.keys(dateFilters).length > 0 && { dateFilters })
      } 
    });
  };

  const handleHospitalClick = (hospitalId) => {
    if (!hospitalId) {
      console.error('Hospital ID is undefined');
      return;
    }

    // Pass current filters via navigation state so HospitalDetails
    // can preserve them when navigating back to the doctors list
    const currentFilters = Object.fromEntries(searchParams.entries());

    navigate(`/hospitals/${hospitalId}`, { 
      state: { 
        hospitalId,
        filters: currentFilters,
      } 
    });
  };

  // Handle specialty selection from carousel
  const handleSpecialtySelect = useCallback((specialtyName) => {
    if (!specialtyName || specialties.length === 0) return;
    
    // Find specialty by name
    const specialty = specialties.find(s => 
      s.name.toLowerCase() === specialtyName.toLowerCase() ||
      s.name.toLowerCase().includes(specialtyName.toLowerCase()) ||
      specialtyName.toLowerCase().includes(s.name.toLowerCase())
    );
    
    if (specialty) {
      console.log('🔄 Specialty selected from carousel:', specialty.name, specialty.id);
      setSelectedSpecialty(specialty.id);
      setCurrentPage(1); // Reset to first page when filter changes
    } else {
      console.warn('Specialty not found:', specialtyName);
    }
  }, [specialties]);

  // Handle "View All Specialities" button click
  const handleViewAllSpecialities = useCallback(() => {
    // Clear specialty filter and navigate to find-doctors if not already there
    setSelectedSpecialty('');
    setCurrentPage(1);
    
    // Navigate to find-doctors if not already on that page
    if (location.pathname !== '/find-doctors') {
      navigate('/find-doctors');
    }
  }, [navigate, location.pathname]);

  // Handle date selection from DateRelative component
  const handleDateRangeSelect = useCallback((dateRange) => {
    if (!dateRange) return;

    if (dateRange.availableDate) {
      setAvailableDate(dateRange.availableDate);
      setAvailableDateStart('');
      setAvailableDateEnd('');
    } else {
      setAvailableDate('');
      setAvailableDateStart(dateRange.availableDateStart || '');
      setAvailableDateEnd(dateRange.availableDateEnd || '');
    }

    // Reset pagination when filters change
    setCurrentPage(1);
  }, []);

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Helper function to get doctor rating
  const getDoctorRating = (doctor) => {
    if (doctor.rating) return doctor.rating;
    // Generate rating for doctors
    const baseRating = 4.2;
    const experienceBonus = doctor.experience_years > 10 ? 0.4 : doctor.experience_years > 5 ? 0.2 : 0;
    return Math.round((baseRating + experienceBonus + Math.random() * 0.6) * 10) / 10;
  };

  // Helper function to get review count
  const getReviewCount = (doctor) => {
    if (doctor.review_count) return doctor.review_count;
    return Math.floor(Math.random() * 200 + 50);
  };

  // Helper function to validate and transform doctor data
  const validateAndTransformDoctor = (doctor) => {
    if (!doctor || typeof doctor !== 'object') {
      console.warn('Invalid doctor data:', doctor);
      return null;
    }

    // Ensure required fields have default values
    const transformedDoctor = {
      id: doctor.id || null,
      name: doctor.name || 'Unknown Doctor',
      specialty_name: doctor.specialty_name || doctor.specialty?.name || 'General Medicine',
      qualifications: doctor.qualifications || { name: 'N/A', abbreviation: 'N/A' },
      experience_years: doctor.experience_years || 0,
      consultation_fee: doctor.consultation_fee || 0,
      service_provider_name: doctor.service_provider_name || doctor.service_provider?.name || 'Unknown Provider',
      profile_photo: doctor.profile_photo || null,
      rating: doctor.rating || null,
      review_count: doctor.review_count || null,
      discount_percentage: doctor.discount_percentage || 0,
      ...doctor // Keep all original data
    };

    return transformedDoctor;
  };

  // Helper function to validate doctors array
  const validateDoctorsArray = (doctorsData) => {
    if (!Array.isArray(doctorsData)) {
      console.warn('Doctors data is not an array:', doctorsData);
      return [];
    }

    return doctorsData
      .map(validateAndTransformDoctor)
      .filter(doctor => doctor !== null);
  };

  // Helper function to generate cache key for current filters
  const generateCacheKey = (filters) => {
    return JSON.stringify({
      search: filters.search || '',
      specialty: filters.specialty || '',
      serviceProvider: filters.serviceProvider || '',
      qualifications: filters.qualifications || '',
      ordering: filters.ordering || 'name',
      page: filters.page || 1,
      pageSize: filters.pageSize || 12
    });
  };

  // Helper function to check if cache is still valid (5 minutes)
  const isCacheValid = (cacheTime) => {
    const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
    return Date.now() - cacheTime < CACHE_DURATION;
  };

  // Applied filters summary used across tabs
  const AppliedFilters = () => {
    if (activeFilters.length === 0) return null;

    return (
      <Paper 
        elevation={0}
        sx={{ 
          p: { xs: 1.5, md: 3 }, 
          mb: { xs: 1.5, md: 3 },
          borderRadius: { xs: '12px', md: '20px' },
          background: 'linear-gradient(135deg, #e8f5e8 0%, #f1f8e9 100%)',
          border: '1px solid rgba(76, 175, 80, 0.2)',
          boxShadow: '0 8px 32px rgba(76, 175, 80, 0.1)',
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={{ xs: 1, md: 2 }}>
          <Typography 
            variant="h6" 
            fontWeight={700} 
            color="text.primary" 
            sx={{ fontSize: { xs: '0.85rem', md: '1.1rem' } }}
          >
            Applied Filters:
          </Typography>
          <Button
            size="small"
            onClick={clearAllFilters}
            startIcon={<ClearIcon />}
            sx={{
              borderRadius: '12px',
              textTransform: 'none',
              fontWeight: 600,
              bgcolor: 'white',
              color: 'error.main',
              fontSize: { xs: '0.75rem', md: '0.875rem' },
              px: { xs: 1, md: 2 },
              py: { xs: 0.5, md: 1 },
              minWidth: 'auto',
              '&:hover': {
                bgcolor: 'error.light + 20',
              },
              '& .MuiButton-startIcon': {
                marginRight: { xs: 0.5, md: 1 },
                '& > *:nth-of-type(1)': {
                  fontSize: { xs: '14px', md: '18px' },
                }
              }
            }}
          >
            Clear All
          </Button>
        </Box>
        <Box display="flex" flexWrap="wrap" gap={{ xs: 0.75, md: 1.5 }}>
          {activeFilters.map((filter, index) => (
            <Chip
              key={index}
              label={filter.label}
              onDelete={() => clearFilter(filter.type)}
              size="small"
              sx={{
                borderRadius: { xs: '8px', md: '12px' },
                bgcolor: 'white',
                color: 'success.main',
                border: '1px solid rgba(76, 175, 80, 0.3)',
                fontWeight: 600,
                fontSize: { xs: '0.7rem', md: '0.9rem' },
                height: { xs: '24px', md: '32px' },
                '&:hover': {
                  bgcolor: 'success.light + 20',
                },
                '& .MuiChip-label': {
                  px: { xs: 1, md: 1.5 },
                }
              }}
              deleteIcon={<CloseIcon sx={{ fontSize: { xs: 14, md: 18 } }} />}
            />
          ))}
        </Box>
      </Paper>
    );
  };

  // Left Sidebar Filters Component
  const LeftSidebarFilters = () => (
    <Paper 
      elevation={0}
      sx={{ 
        p: 3, 
        height: 'fit-content', 
        position: 'sticky', 
        top: 20,
        borderRadius: '20px',
        background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
        border: '1px solid rgba(0,0,0,0.05)',
        boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography 
          variant="h6" 
          sx={{ 
            fontWeight: 700,
            fontSize: '1.2rem',
            background: 'linear-gradient(135deg, primary.main 30%, secondary.main 90%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          Filters
        </Typography>
        <Button
          size="small"
          onClick={clearAllFilters}
          startIcon={<ClearIcon />}
          disabled={activeFilters.length === 0}
          sx={{
            borderRadius: '12px',
            textTransform: 'none',
            fontWeight: 600,
            bgcolor: activeFilters.length > 0 ? 'error.light + 20' : 'transparent',
            color: activeFilters.length > 0 ? 'error.main' : 'text.secondary',
            '&:hover': {
              bgcolor: activeFilters.length > 0 ? 'error.light + 30' : 'transparent',
            }
          }}
        >
          Clear All
        </Button>
      </Box>
      
      <Divider sx={{ mb: 3, opacity: 0.6 }} />
      
      {/* Sort By */}
      <Box mb={3}>
        <Typography variant="subtitle1" fontWeight={700} mb={2} color="text.primary" sx={{ fontSize: '1rem' }}>
          Sort By
        </Typography>
        <FormControl fullWidth size="medium">
          <Select
            value={sortBy}
            onChange={(e) => {
              console.log('🔄 Sort changed to:', e.target.value);
              setSortBy(e.target.value);
            }}
            sx={{
              borderRadius: '16px',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(0,0,0,0.12)',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'primary.main',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: 'primary.main',
              },
            }}
          >
            {getSortOptions(selectedList === 'doctors' ? 'doctors' : 'hospitals', userLocation).map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Service Provider */}
      <Box mb={3}>
        <Typography variant="subtitle1" fontWeight={700} mb={2} color="text.primary" sx={{ fontSize: '1rem' }}>
          Service Provider
        </Typography>
        <FormControl fullWidth size="medium">
          <Select
            value={selectedServiceProvider}
            onChange={(e) => {
              console.log('🔄 Service Provider changed to:', e.target.value);
              setSelectedServiceProvider(e.target.value);
            }}
            sx={{
              borderRadius: '16px',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(0,0,0,0.12)',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'primary.main',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: 'primary.main',
              },
            }}
          >
            <MenuItem value="">All Providers</MenuItem>
            {serviceProviders.map((provider) => (
              <MenuItem key={provider.id} value={provider.id}>
                {provider.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Qualifications */}
      <Box mb={3}>
        <Typography variant="subtitle1" fontWeight={700} mb={2} color="text.primary" sx={{ fontSize: '1rem' }}>
          Qualifications
        </Typography>
        <FormControl fullWidth size="medium">
          <Select
            value={selectedQualifications}
            onChange={(e) => setSelectedQualifications(e.target.value)}
            sx={{
              borderRadius: '16px',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(0,0,0,0.12)',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'primary.main',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: 'primary.main',
              },
            }}
          >
            <MenuItem value="">All Qualifications</MenuItem>
            {commonQualifications.map((qual) => (
              <MenuItem key={qual} value={qual}>
                {qual}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Date Filters */}
      <Box mb={3}>
        <Typography variant="subtitle1" fontWeight={700} mb={2} color="text.primary" sx={{ fontSize: '1rem' }}>
          Available Date
        </Typography>
        <TextField
          fullWidth
          size="medium"
          type="date"
          label="Single Date"
          value={availableDate}
          onChange={(e) => {
            setAvailableDate(e.target.value);
            setAvailableDateStart('');
            setAvailableDateEnd('');
          }}
          InputLabelProps={{ shrink: true }}
          sx={{ mb: 2 }}
        />
        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block', textAlign: 'center' }}>
          OR
        </Typography>
        <TextField
          fullWidth
          size="medium"
          type="date"
          label="Start Date"
          value={availableDateStart}
          onChange={(e) => {
            setAvailableDateStart(e.target.value);
            setAvailableDate('');
          }}
          InputLabelProps={{ shrink: true }}
          sx={{ mb: 2 }}
        />
        <TextField
          fullWidth
          size="medium"
          type="date"
          label="End Date"
          value={availableDateEnd}
          onChange={(e) => {
            setAvailableDateEnd(e.target.value);
            setAvailableDate('');
          }}
          InputLabelProps={{ shrink: true }}
        />
      </Box>
    </Paper>
  );

  // Mobile Filter Drawer Content
  const FilterDrawerContent = () => (
    <Box sx={{ width: '100%', p: 2 }}>
      {/* Drag Handle */}
      <Box 
        sx={{ 
          width: 40, 
          height: 4, 
          bgcolor: 'grey.300', 
          borderRadius: 2, 
          mx: 'auto', 
          mb: 3,
          cursor: 'grab',
          '&:active': { cursor: 'grabbing' }
        }} 
      />
      
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography 
          variant="h6"
          sx={{ 
            fontWeight: 700,
            fontSize: '1.1rem',
            color: 'text.primary',
          }}
        >
          Filters for {selectedList === 'doctors' ? 'Doctors' : selectedList === 'hospitals' ? 'Hospitals' : 'Clinics'}
        </Typography>
        <Button
          size="small"
          onClick={clearAllFilters}
          sx={{
            borderRadius: '8px',
            textTransform: 'none',
            fontWeight: 600,
            bgcolor: 'grey.100',
            color: 'text.secondary',
            '&:hover': {
              bgcolor: 'grey.200',
            }
          }}
        >
          Clear All
        </Button>
      </Box>
      
      {/* Sort By */}
      <Box mb={2}>
        <Typography variant="subtitle2" fontWeight={600} mb={1} color="text.primary">
          Sort By
        </Typography>
          <FormControl fullWidth size="small">
            <Select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            sx={{
              borderRadius: '8px',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'grey.300',
              },
            }}
            >
              {getSortOptions(selectedList === 'doctors' ? 'doctors' : 'hospitals', userLocation).map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
      </Box>

      {/* Service Provider - Only show for doctors */}
      {selectedList === 'doctors' && (
        <Box mb={2}>
          <Typography variant="subtitle2" fontWeight={600} mb={1} color="text.primary">
            Service Provider
          </Typography>
          <FormControl fullWidth size="small">
            <Select
              value={selectedServiceProvider}
              onChange={(e) => setSelectedServiceProvider(e.target.value)}
              sx={{
                borderRadius: '8px',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'grey.300',
                },
              }}
            >
              <MenuItem value="">All Providers</MenuItem>
              {serviceProviders.map((provider) => (
                <MenuItem key={provider.id} value={provider.id}>
                  {provider.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}

      {/* Qualifications - Only show for doctors */}
      {selectedList === 'doctors' && (
        <Box mb={3}>
          <Typography variant="subtitle2" fontWeight={600} mb={1} color="text.primary">
            Qualifications
          </Typography>
          <FormControl fullWidth size="small">
            <Select
              value={selectedQualifications}
              onChange={(e) => setSelectedQualifications(e.target.value)}
              sx={{
                borderRadius: '8px',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'grey.300',
                },
              }}
            >
              <MenuItem value="">All Qualifications</MenuItem>
              {commonQualifications.map((qual) => (
                <MenuItem key={qual} value={qual}>
                  {qual}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}

      {/* Date Filters */}
      <Box mb={3}>
        <Typography variant="subtitle2" fontWeight={600} mb={1} color="text.primary">
          Available Date
        </Typography>
        <TextField
          fullWidth
          size="small"
          type="date"
          label="Single Date"
          value={availableDate}
          onChange={(e) => {
            setAvailableDate(e.target.value);
            setAvailableDateStart('');
            setAvailableDateEnd('');
          }}
          InputLabelProps={{ shrink: true }}
          sx={{ mb: 2 }}
        />
        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block', textAlign: 'center' }}>
          OR
        </Typography>
        <TextField
          fullWidth
          size="small"
          type="date"
          label="Start Date"
          value={availableDateStart}
          onChange={(e) => {
            setAvailableDateStart(e.target.value);
            setAvailableDate('');
          }}
          InputLabelProps={{ shrink: true }}
          sx={{ mb: 2 }}
        />
        <TextField
          fullWidth
          size="small"
          type="date"
          label="End Date"
          value={availableDateEnd}
          onChange={(e) => {
            setAvailableDateEnd(e.target.value);
            setAvailableDate('');
          }}
          InputLabelProps={{ shrink: true }}
        />
      </Box>
    </Box>
  );

  if ((loadingInitial || loadingDoctors) && doctors.length === 0) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="60vh"
        sx={{
          background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
        }}
      >
        <CircularProgress size={60} sx={{ color: '#1976d2' }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        minHeight: '100dvh',
        bgcolor: theme.palette.background.default
      }}
    >
    
      <Container
        maxWidth="xxl"
        sx={{
          pt: isMobile ? `${Math.max(safeAreaInsets.top, 8)}px` : '8px',
          pb: isMobile ? `${Math.max(safeAreaInsets.bottom + 100, 116)}px` : '16px',
          px: { xs: 1, sm: 2, md: 2 }
        }}
      >
        {/* Button Group for List Selection - Mobile Only */}
        <Box 
          display={{ xs: 'flex', md: 'none' }}
          justifyContent="center" 
          alignItems="center" 
          flexDirection="row"
          gap={0.5}
          flexWrap="nowrap"
          mb={1.5}
          sx={{
            width: '100%',
            maxWidth: '380px',
            mx: 'auto',
            p: 1.5,
            bgcolor: 'white',
            borderRadius: '24px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            border: '1px solid rgba(0,0,0,0.05)',
          }}
        >
          <Button
            variant={selectedList === 'doctors' ? 'contained' : 'outlined'}
            color="primary"
            onClick={() => handleListChange('doctors')}
            sx={{
              borderRadius: '12px',
              fontWeight: 700,
              minWidth: 0,
              width: '100%',
              height: '72px',
              flex: 1,
              textTransform: 'none',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.25,
              py: 1,
              px: 0.5,
              fontSize: '0.7rem',
              transition: 'all 0.3s ease-in-out',
              ...(selectedList === 'doctors' ? {
                background: 'linear-gradient(135deg, primary.main 30%, secondary.main 90%)',
                boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
              '&:hover': {
                transform: 'translateY(-1px)',
                  boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
                }
              } : {
                '&:hover': {
                  transform: 'translateY(-1px)',
                  boxShadow: '0 2px 12px rgba(0,0,0,0.1)',
                }
              })
            }}
          >
            <FaUserDoctor 
              style={{ 
                fontSize: 20,
                color: selectedList === 'doctors' ? 'white' : undefined
              }} 
            />
            <Typography sx={{ fontWeight: 700, fontSize: '0.7rem', lineHeight: 1.2 }}>
              Doctors
            </Typography>
          </Button>
          <Button
            variant={selectedList === 'hospitals' ? 'contained' : 'outlined'}
            color="primary"
            onClick={() => handleListChange('hospitals')}
            sx={{
              borderRadius: '12px',
              fontWeight: 700,
              minWidth: 0,
              width: '100%',
              height: '72px',
              flex: 1,
              textTransform: 'none',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.25,
              py: 1,
              px: 0.5,
              fontSize: '0.7rem',
              transition: 'all 0.3s ease-in-out',
              ...(selectedList === 'hospitals' ? {
                background: 'linear-gradient(135deg, primary.main 30%, secondary.main 90%)',
                boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
              '&:hover': {
                transform: 'translateY(-1px)',
                  boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
                }
              } : {
                '&:hover': {
                  transform: 'translateY(-1px)',
                  boxShadow: '0 2px 12px rgba(0,0,0,0.1)',
                }
              })
            }}
          >
            {selectedList === 'hospitals' ? (
              <RiHospitalFill 
                style={{ 
                  fontSize: 20,
                  color: 'white'
                }} 
              />
            ) : (
              <RiHospitalLine 
                style={{ 
                  fontSize: 20
                }} 
              />
            )}
            <Typography sx={{ fontWeight: 700, fontSize: '0.7rem', lineHeight: 1.2 }}>
              Hospitals
            </Typography>
          </Button>
          <Button
            variant={selectedList === 'saved' ? 'contained' : 'outlined'}
            color="primary"
            onClick={() => handleListChange('saved')}
            sx={{
              borderRadius: '12px',
              fontWeight: 700,
              minWidth: 0,
              width: '100%',
              height: '72px',
              flex: 1,
              textTransform: 'none',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.25,
              py: 1,
              px: 0.5,
              fontSize: '0.7rem',
              transition: 'all 0.3s ease-in-out',
              ...(selectedList === 'saved' ? {
                background: 'linear-gradient(135deg, primary.main 30%, secondary.main 90%)',
                boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
              '&:hover': {
                transform: 'translateY(-1px)',
                  boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
                }
              } : {
                '&:hover': {
                  transform: 'translateY(-1px)',
                  boxShadow: '0 2px 12px rgba(0,0,0,0.1)',
                }
              })
            }}
          >
            {selectedList === 'saved' ? (
              <IoBookmarks 
                style={{ 
                  fontSize: 20,
                  color: 'white'
                }} 
              />
            ) : (
              <IoBookmarksOutline 
                style={{ 
                  fontSize: 20
                }} 
              />
            )}
            <Typography sx={{ fontWeight: 700, fontSize: '0.7rem', lineHeight: 1.2 }}>
              Favourites
            </Typography>
          </Button>
        </Box>

        {/* Shared Filters Bar for doctors and hospitals */}
        {(selectedList === 'doctors' || selectedList === 'hospitals') && (
          <FiltersBar
            type={selectedList === 'doctors' ? 'doctors' : 'hospitals'}
            sortBy={sortBy}
            onSortChange={setSortBy}
            onSpecialtySelect={handleSpecialtySelect}
            onViewAllSpecialities={handleViewAllSpecialities}
            onSearchChange={setSearchTerm}
            onFilterClick={() => setFilterDrawerOpen(true)}
            searchTerm={searchTerm}
            searchBarOpen={searchBarOpen}
            onSearchBarToggle={() => setSearchBarOpen(!searchBarOpen)}
            userLocation={userLocation}
            isMobile={isMobile}
            selectedSpecialty={selectedSpecialty}
            showDate
            onDateRangeSelect={handleDateRangeSelect}
          />
        )}

        {/* Render the selected list */}
        {selectedList === 'doctors' && (
          <>
            {/* Header */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
              {/* <Box>
                <Typography 
                  variant="h3" 
                  component="h1"
                  sx={{
                    fontWeight: 700,
                    background: 'linear-gradient(45deg, #1976d2, #42a5f5)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    mb: 0.5,
                  }}
                >
                  Find Doctors
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500 }}>
                  Discover and book appointments with qualified healthcare professionals
                </Typography>
              </Box> */}
            </Box>

            {/* Welcome Alert */}
            {/* {user && (
              <Alert 
                severity="info" 
                sx={{ 
                  mb: 3,
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #e3f2fd 0%, #f3f8ff 100%)',
                  border: '1px solid rgba(25, 118, 210, 0.2)',
                }}
              >
                Welcome back, <strong>{user.name || user.email}</strong>! Ready to find your perfect doctor?
              </Alert>
            )} */}

            {/* Error Alert */}
            {error && (
              <Alert 
                severity="error" 
                sx={{ 
                  mb: 3,
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #ffebee 0%, #fff5f5 100%)',
                  border: '1px solid rgba(211, 47, 47, 0.2)',
                }}
                action={
                  <Button
                    color="inherit"
                    size="small"
                    onClick={retryFetch}
                    disabled={isRetrying}
                    sx={{
                      fontWeight: 600,
                      textTransform: 'none',
                    }}
                  >
                    {isRetrying ? 'Retrying...' : 'Retry'}
                  </Button>
                }
              >
                {error}
                {retryCount > 0 && (
                  <Typography variant="caption" display="block" sx={{ mt: 1, opacity: 0.8 }}>
                    Retry attempt: {retryCount}
                  </Typography>
                )}
              </Alert>
            )}


            {/* Main Content */}
            <Grid container spacing={{ xs: 1.5, sm: 2, md: 2.5 }} sx={{ mt: { xs: 0.5, md: 0 } }}>
              {/* Left Sidebar - Filters (Desktop) */}
              {!isMobile && (
                <Grid item xs={12} md={3}>
                  <LeftSidebarFilters />
                </Grid>
              )}

              {/* Right Side - Results */}
              <Grid item xs={12} md={9}>
                {/* Active Filters Summary */}
                <AppliedFilters />

                {/* Results Summary */}
                {/* <Box 
                  display="flex" 
                  justifyContent="space-between" 
                  alignItems="center" 
                  mb={3}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: 'white',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                  }}
                >
                  <Typography variant="body1" fontWeight={600} color="text.primary">
                    {totalCount > 0 ? `${totalCount} doctor${totalCount !== 1 ? 's' : ''} found` : 'No doctors found'}
                  </Typography>
                </Box> */}

                {/* Doctors Grid */}
                <Grid container spacing={{ xs: 1.5, sm: 2, md: 2 }}>
                  {loadingDoctors ? (
                    // Skeleton loading for doctors
                    Array.from({ length: 6 }).map((_, index) => (
                      <Grid item xs={12} sm={6} lg={4} key={index}>
                        <Card 
                          sx={{ 
                            height: '100%',
                            borderRadius: 3,
                            background: 'linear-gradient(135deg, #ffffff 0%, #fafafa 100%)',
                            border: '1px solid rgba(0,0,0,0.08)',
                            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                          }}
                        >
                          <CardContent sx={{ p: { xs: 2, md: 3 }, height: '100%', display: 'flex', flexDirection: 'column' }}>
                            {/* Header - Doctor Info Skeleton */}
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                              <Skeleton variant="circular" width={70} height={70} />
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Skeleton variant="text" width="80%" height={24} sx={{ mb: 0.5 }} />
                                <Skeleton variant="text" width="60%" height={20} sx={{ mb: 0.5 }} />
                                <Skeleton variant="text" width="40%" height={16} />
                              </Box>
                            </Box>

                            {/* Qualifications & Experience Skeleton */}
                            <Box sx={{ mb: 2 }}>
                              <Skeleton variant="text" width="90%" height={16} sx={{ mb: 0.5 }} />
                            </Box>
                            
                            {/* Location Skeleton */}
                            <Box sx={{ mb: 2 }}>
                              <Skeleton variant="text" width="70%" height={16} />
                            </Box>
                            
                            {/* Consultation Fee Skeleton */}
                            <Box sx={{ mb: 2 }}>
                              <Skeleton variant="text" width="50%" height={20} />
                            </Box>

                            {/* Action Button Skeleton */}
                            <Box sx={{ mt: 'auto' }}>
                              <Skeleton variant="rectangular" height={40} sx={{ borderRadius: 2 }} />
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))
                  ) : doctors.length === 0 && initialDoctorsFetched ? (
                    <Grid item xs={12} mb={2}>
                      <Box 
                        textAlign="center" 
                        py={2}
                        sx={{
                          bgcolor: 'white',
                          borderRadius: 3,
                          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                        }}
                      >
                        <Typography variant="h5" color="text.secondary" gutterBottom fontWeight={600}>
                          No doctors found
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                          Try adjusting your search criteria or filters
                        </Typography>
                      </Box>
                    </Grid>
                  ) : (
                    doctors.map((doctor, index) => (
                        <Grid item xs={12} sm={6} lg={4} key={doctor.id}>
                        <DoctorCard 
                          doctor={doctor} 
                          index={index} 
                          onClick={handleDoctorClick}
                        />
                        </Grid>
                    ))
                  )}
                </Grid>

                {/* Pagination */}
                {totalPages > 1 && (
                  <Box 
                    display="flex" 
                    justifyContent="center" 
                    mt={2}
                    sx={{
                      p: 4,
                      bgcolor: 'white',
                      borderRadius: '20px',
                      boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                      border: '1px solid rgba(0,0,0,0.05)',
                    }}
                  >
                    <Pagination
                      count={totalPages}
                      page={currentPage}
                      onChange={handlePageChange}
                      color="primary"
                      size={isMobile ? "small" : "medium"}
                      sx={{
                        '& .MuiPaginationItem-root': {
                          borderRadius: '12px',
                          fontWeight: 600,
                          fontSize: '1rem',
                        },
                        '& .Mui-selected': {
                          background: 'linear-gradient(135deg, primary.main 30%, secondary.main 90%)',
                          color: 'white',
                          '&:hover': {
                            background: 'linear-gradient(135deg, primary.dark 30%, secondary.dark 90%)',
                          }
                        }
                      }}
                    />
                  </Box>
                )}
              </Grid>
            </Grid>

          </>
        )}
        {selectedList === 'clinics' && (
          <Box sx={{ width: '100%' }}>
            <ClinicList />
          </Box>
        )}
        {selectedList === 'saved' && (
          <Box sx={{ width: '100%', mt: { xs: 1, md: 0 } }}>
            {/* Saved Favorites */}
            {loadingSaved ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
                <CircularProgress size={50} />
              </Box>
            ) : savedError ? (
              <Alert severity="error" sx={{ maxWidth: 600, mx: 'auto', mb: 3 }}>
                {savedError}
              </Alert>
            ) : (
              <>
                {/* Saved Doctors Section */}
                {savedDoctors.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, color: 'text.primary' }}>
                      Saved Doctors ({savedDoctors.length})
                    </Typography>
                    <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }}>
                      {savedDoctors.map((doctor, index) => (
                        <Grid item xs={12} sm={6} lg={4} key={doctor.id}>
                          <DoctorCard 
                            doctor={doctor} 
                            index={index} 
                            onClick={handleDoctorClick}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                )}

                {/* Saved Hospitals Section */}
                {savedHospitals.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, color: 'text.primary' }}>
                      Saved Hospitals ({savedHospitals.length})
                    </Typography>
                    <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }}>
                      {savedHospitals.map((hospital, index) => (
                        <Grid item xs={12} sm={6} md={4} key={hospital.id}>
                          <HospitalCard 
                            hospital={hospital} 
                            index={index}
                            onViewDoctors={handleHospitalClick}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                )}


                {/* Empty State */}
                {savedDoctors.length === 0 && savedHospitals.length === 0 && (
                  <Box 
                    sx={{ 
                      textAlign: 'center', 
                      py: 8,
                      bgcolor: 'white',
                      borderRadius: 3,
                      boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                    }}
                  >
                    <IoBookmarksOutline style={{ fontSize: 80, color: '#9e9e9e', marginBottom: 16 }} />
                    <Typography variant="h5" color="text.secondary" gutterBottom fontWeight={600}>
                      No saved favorites yet
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                      Start saving your favorite doctors and hospitals by clicking the bookmark icon
                    </Typography>
                    <Button
                      variant="contained"
                      onClick={() => handleListChange('doctors')}
                      sx={{
                        borderRadius: 3,
                        textTransform: 'none',
                        fontWeight: 600,
                        px: 4,
                        py: 1.5,
                      }}
                    >
                      Browse Doctors
                    </Button>
                  </Box>
                )}
              </>
            )}
          </Box>
        )}
        {selectedList === 'hospitals' && (
          <Box sx={{ width: '100%', mt: { xs: 1, md: 0 } }}>

            <AppliedFilters />
            {/* Hospitals Grid */}
            <Grid container spacing={{ xs: 1.5, sm: 2, md: 2 }} sx={{ mt: { xs: 1, md: 1.5 } }}>
              {loadingDoctors ? (
                // Loading skeletons
                Array.from({ length: 6 }).map((_, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <Card sx={{ height: '100%', borderRadius: '24px' }}>
                      <Skeleton variant="rectangular" height={200} />
                      <CardContent>
                        <Skeleton variant="text" height={40} />
                        <Skeleton variant="text" height={20} />
                        <Skeleton variant="text" height={20} />
                      </CardContent>
                    </Card>
                  </Grid>
                ))
              ) : hospitals.length > 0 ? (
                hospitals.map((hospital, index) => (
                  <Grid item xs={12} sm={6} md={4} key={hospital.id}>
                    <HospitalCard
                      hospital={hospital}
                      index={index}
                      onViewDoctors={handleHospitalClick}
                      selectedSpecialty={selectedSpecialty}
                    />
                  </Grid>
                ))
              ) : (
                // No results
                <Grid item xs={12}>
                  <Card sx={{ 
                    p: 4, 
                    textAlign: 'center', 
                    borderRadius: '24px',
                    background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(248,250,252,0.8) 100%)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255,255,255,0.2)',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                  }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                      No hospitals found
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Try adjusting your search criteria or filters
                    </Typography>
                  </Card>
                </Grid>
              )}
            </Grid>

            {/* Pagination */}
            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={(event, page) => setCurrentPage(page)}
                  color="primary"
                  size="large"
                  sx={{
                    '& .MuiPaginationItem-root': {
                      borderRadius: '12px',
                      fontWeight: 600,
                    }
                  }}
                />
              </Box>
            )}
          </Box>
        )}

        {/* Mobile Filter Drawer - Bottom Sheet - Available for all tabs */}
        <Drawer
          anchor="bottom"
          open={filterDrawerOpen}
          onClose={() => setFilterDrawerOpen(false)}
          PaperProps={{
            sx: {
              height: '50vh',
              maxHeight: '50vh',
              borderTopLeftRadius: '20px',
              borderTopRightRadius: '20px',
              background: 'white',
              boxShadow: '0 -8px 32px rgba(0,0,0,0.1)',
            }
          }}
          ModalProps={{
            keepMounted: true,
          }}
        >
          <FilterDrawerContent />
        </Drawer>
      </Container>
    </Box>
  );
};

export default FindDoctors;

